import { ActionMenuItem } from 'components/ActionMenu';
import { checkIsCsvIntegration } from 'utils/integration';
import { IntegrationState } from 'types/integration';

export const CSV_ACTION_NOT_SUPPORTED_TOOLTIP = 'Not supported for CSV import integrations.';
export const CSV_ALREADY_EXISTS_TOOLTIP = 'This team already has a CSV import integration.';
/** While the team's integrations are loading or failed to load, a second csv-agent can't be ruled out. */
export const CSV_TEAM_INTEGRATIONS_UNKNOWN_TOOLTIP =
  "Can't check this team's other CSV import integrations yet. Try again in a moment.";

export type IntegrationTransitionAction =
  'suspend' | 'archive' | 'activate' | 'unarchive' | 'delete';

export type IntegrationMenuHandlers = {
  onRetroactivePhaseOrder: () => void;
  onCheckOdbcCredentials: () => void;
  onChangeSyncFrequency: () => void;
  onEditConnectionInfo: () => void;
  onTransition: (action: IntegrationTransitionAction) => void;
};

/**
 * Builds the Integration List row menu for an integration in `state`.
 *
 * csv-agent (GLX-2091): the sync actions (Retroactive Phase Order, Change Sync
 * Frequency, Suspend, Archive) stay visible but disabled with a tooltip, and
 * Unarchive is disabled when the team already has a non-archived csv-agent, or
 * while that can't be ruled out. Every other service gets the same items as before.
 */
export const getIntegrationMenuItems = ({
  state,
  targetService,
  isAjeraIntegration,
  csvUnarchiveBlockedReason,
  handlers,
}: {
  state?: IntegrationState;
  targetService?: string;
  isAjeraIntegration: boolean;
  /**
   * Why Unarchive on this csv-agent is blocked (CSV_ALREADY_EXISTS_TOOLTIP or
   * CSV_TEAM_INTEGRATIONS_UNKNOWN_TOOLTIP); undefined when it is allowed.
   */
  csvUnarchiveBlockedReason?: string;
  handlers: IntegrationMenuHandlers;
}): ActionMenuItem[] => {
  const isCsv = checkIsCsvIntegration(targetService);
  const csvDisabled: Partial<ActionMenuItem> = isCsv
    ? { disabled: true, tooltip: CSV_ACTION_NOT_SUPPORTED_TOOLTIP }
    : {};

  // Sync frequency edits require the service slug for the IS endpoint, so
  // only offer it when targetService is known (GLX-1609).
  const changeSyncFrequencyItem: ActionMenuItem[] = targetService
    ? [
        {
          label: 'Change Sync Frequency',
          onClick: handlers.onChangeSyncFrequency,
          ...csvDisabled,
        },
      ]
    : [];

  switch (state) {
    case IntegrationState.DRAFT:
      return [
        ...(targetService?.includes('hosted')
          ? [{ label: 'Edit Connection Info', onClick: handlers.onEditConnectionInfo }]
          : []),
        { label: 'Delete', onClick: () => handlers.onTransition('delete') },
      ];

    case IntegrationState.ACTIVE:
      return [
        {
          label: 'Retroactive Phase Order',
          onClick: handlers.onRetroactivePhaseOrder,
          ...csvDisabled,
        },
        ...(isAjeraIntegration
          ? [
              {
                label: 'Check ODBC Credential Validity',
                onClick: handlers.onCheckOdbcCredentials,
              },
            ]
          : []),
        ...changeSyncFrequencyItem,
        { label: 'Suspend', onClick: () => handlers.onTransition('suspend'), ...csvDisabled },
        { label: 'Archive', onClick: () => handlers.onTransition('archive'), ...csvDisabled },
      ];

    case IntegrationState.SUSPENDED:
      return [
        { label: 'Activate', onClick: () => handlers.onTransition('activate') },
        ...changeSyncFrequencyItem,
        { label: 'Archive', onClick: () => handlers.onTransition('archive'), ...csvDisabled },
      ];

    case IntegrationState.ARCHIVED:
      return [
        {
          label: 'Unarchive',
          onClick: () => handlers.onTransition('unarchive'),
          ...(isCsv && csvUnarchiveBlockedReason
            ? { disabled: true, tooltip: csvUnarchiveBlockedReason }
            : {}),
        },
        { label: 'Delete', onClick: () => handlers.onTransition('delete') },
      ];

    default:
      // No known state (e.g. integration not yet loaded) — no actions.
      return [];
  }
};
